import { useCallback, useEffect, useState } from "react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Percent, DownloadSimple, ArrowsClockwise, Buildings } from "@phosphor-icons/react";

const money = (v) => `£${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthLabel = (m) => {
  if (!m || m.length < 7) return m || "—";
  const d = new Date(`${m}-01T00:00:00Z`);
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
};

function Tile({ label, value, sub, testid }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <p className="text-2xl font-bold font-[Chivo] mt-1">{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

async function downloadCsv(month) {
  const res = await API.get("/platform/fees/export.csv", { params: month ? { month } : {}, responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `fixlr-platform-fees-${month || "all-time"}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function PlatformFeesPage() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState("all");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await API.get("/platform/fees", { params: { months: 24 } });
      setData(res);
    } catch { toast.error("Failed to load platform fees"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async (m) => {
    setExporting(m || "all");
    try { await downloadCsv(m); toast.success(`CSV exported (${m || "all time"})`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
    finally { setExporting(""); }
  };

  if (!data) return <div className="text-center py-12 text-muted-foreground text-sm">Loading platform fees...</div>;
  const months = month === "all" ? data.months : data.months.filter((m) => m.month === month);
  const shown = month === "all" ? data.totals : (months[0] || { count: 0, gross: 0, refunded: 0, fees: 0, fees_refunded: 0, fees_net: 0 });

  return (
    <div className="space-y-6" data-testid="platform-fees-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight flex items-center gap-2"><Percent size={28} weight="duotone" className="text-primary" /> Platform Fees</h1>
          <p className="text-sm text-muted-foreground mt-1">Application fees earned on tenants' Stripe Connect card payments — currently {data.fee_pct}% per payment.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-44" data-testid="fees-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All months</SelectItem>
              {data.months.map((m) => <SelectItem key={m.month} value={m.month}>{monthLabel(m.month)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="fees-reload-btn"><ArrowsClockwise size={15} className={loading ? "animate-spin" : ""} /></Button>
          <Button onClick={() => exportCsv(month === "all" ? null : month)} disabled={!!exporting} className="gap-1" data-testid="fees-export-btn">
            <DownloadSimple size={15} /> {exporting ? "Exporting…" : `Export CSV${month === "all" ? " (all time)" : ""}`}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label="Fees earned" value={money(shown.fees_net)} sub={shown.fees_refunded > 0 ? `${money(shown.fees)} charged − ${money(shown.fees_refunded)} returned on refunds` : `${month === "all" ? "All time" : monthLabel(month)}`} testid="fees-tile-net" />
        <Tile label="Card payments" value={shown.count} sub={`${money(shown.gross)} processed`} testid="fees-tile-count" />
        <Tile label="Refunded to clients" value={money(shown.refunded)} sub="Across all tenants" testid="fees-tile-refunded" />
        <Tile label="Tenants using Connect" value={data.tenants_total} sub={`Fee rate ${data.fee_pct}%`} testid="fees-tile-tenants" />
      </div>

      {months.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="fees-empty">
          No Connect card payments yet. Fees appear here as soon as a tenant onboarded through Stripe Connect takes a card payment.
        </div>
      )}

      {months.map((m) => (
        <div key={m.month} className="rounded-lg border border-border overflow-hidden" data-testid={`fees-month-${m.month}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-muted/30">
            <div className="flex items-center gap-3">
              <h2 className="text-base md:text-lg font-semibold">{monthLabel(m.month)}</h2>
              {m.month === data.current_month && <Badge variant="outline">Current month</Badge>}
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span><span className="text-muted-foreground">Payments</span> <strong>{m.count}</strong></span>
              <span><span className="text-muted-foreground">Gross</span> <strong>{money(m.gross)}</strong></span>
              <span><span className="text-muted-foreground">Fees</span> <strong data-testid={`fees-month-net-${m.month}`}>{money(m.fees_net)}</strong>{m.fees_refunded > 0 && <span className="text-[11px] text-muted-foreground"> (−{money(m.fees_refunded)} returned)</span>}</span>
              <Button size="sm" variant="ghost" onClick={() => exportCsv(m.month)} disabled={!!exporting} className="gap-1" data-testid={`fees-export-month-${m.month}`}><DownloadSimple size={14} /> CSV</Button>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-muted-foreground border-t border-border">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Tenant</th>
                <th className="text-right px-4 py-2 font-semibold">Payments</th>
                <th className="text-right px-4 py-2 font-semibold">Gross</th>
                <th className="text-right px-4 py-2 font-semibold">Refunded</th>
                <th className="text-right px-4 py-2 font-semibold">Fees charged</th>
                <th className="text-right px-4 py-2 font-semibold">Fees returned</th>
                <th className="text-right px-4 py-2 font-semibold">Fees earned</th>
              </tr>
            </thead>
            <tbody>
              {m.tenants.map((t) => (
                <tr key={t.tenant_id} className="border-t border-border" data-testid={`fees-tenant-row-${t.tenant_id}`}>
                  <td className="px-4 py-2 flex items-center gap-2"><Buildings size={14} className="text-muted-foreground" /> {t.tenant_name}</td>
                  <td className="px-4 py-2 text-right">{t.count}</td>
                  <td className="px-4 py-2 text-right">{money(t.gross)}</td>
                  <td className="px-4 py-2 text-right text-muted-foreground">{t.refunded > 0 ? money(t.refunded) : "—"}</td>
                  <td className="px-4 py-2 text-right">{money(t.fees)}</td>
                  <td className="px-4 py-2 text-right text-muted-foreground">{t.fees_refunded > 0 ? `−${money(t.fees_refunded)}` : "—"}</td>
                  <td className="px-4 py-2 text-right font-semibold">{money(t.fees_net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
